import React from "react";
import { Box, InputAdornment, TextField } from "@mui/material";

interface ColorFieldProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/** Native colour input needs a 6-digit hex; anything else falls back to white for the swatch. */
const swatchValue = (v?: string): string => (v && /^#[0-9a-f]{6}$/i.test(v.trim()) ? v.trim() : "#ffffff");

/**
 * Hex colour field with an inline native colour-picker swatch. The text field
 * keeps the raw hex editable (so values like `#a85a2a` can be pasted), while the
 * swatch offers a visual picker — both write back through the same `onChange`.
 */
const ColorField: React.FC<ColorFieldProps> = ({ label, value, onChange, placeholder, disabled }) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={value ?? ""}
    placeholder={placeholder}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Box
            component="input"
            type="color"
            aria-label={`${label} picker`}
            value={swatchValue(value)}
            disabled={disabled}
            onChange={(e) => onChange((e.target as HTMLInputElement).value)}
            sx={{
              width: 26,
              height: 26,
              p: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "6px",
              background: "none",
              cursor: disabled ? "default" : "pointer",
              "&::-webkit-color-swatch-wrapper": { p: 0 },
              "&::-webkit-color-swatch": { border: "none", borderRadius: "5px" }
            }}
          />
        </InputAdornment>
      )
    }}
  />
);

export default ColorField;
